// src/components/ShareBox.js
import React, { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  IconCopy,
  IconCheck,
  IconWhatsapp,
  IconEye,
  IconFacebook,
  IconTwitter,
  IconTelegram,
  IconEmail,
  IconQr,
  IconDownload,
  IconLink,
  IconShare,
} from './ui/Icons';

const SOCIALS = [
  { key: 'whatsapp', label: 'WhatsApp', color: '#25D366', Icon: IconWhatsapp,
    buildUrl: (url, title) => `https://wa.me/?text=${encodeURIComponent(`¡Estás invitado a ${title}! Mira la invitación aquí: ${url}`)}` },
  { key: 'facebook', label: 'Facebook', color: '#1877F2', Icon: IconFacebook,
    buildUrl: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
  { key: 'twitter', label: 'X / Twitter', color: '#111827', Icon: IconTwitter,
    buildUrl: (url, title) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(`¡Estás invitado a ${title}!`)}&url=${encodeURIComponent(url)}` },
  { key: 'telegram', label: 'Telegram', color: '#0088cc', Icon: IconTelegram,
    buildUrl: (url, title) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(`¡Estás invitado a ${title}!`)}` },
  { key: 'email', label: 'Email', color: '#4b5563', Icon: IconEmail,
    buildUrl: (url, title) => `mailto:?subject=${encodeURIComponent(`Invitación: ${title}`)}&body=${encodeURIComponent(`Te comparto la invitación: ${url}`)}` },
];

export default function ShareBox({ shortCode, title }) {
  const publicUrl = `${process.env.REACT_APP_PUBLIC_URL}/i/${shortCode}`;
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const qrRef = useRef(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
    } catch {
      const input = document.createElement('input');
      input.value = publicUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: `¡Estás invitado a ${title}!`, url: publicUrl });
      } catch { /* cancelado */ }
    } else {
      copy();
    }
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `invitacion-${shortCode}.png`;
    a.click();
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      {/* ============================ HEADER ============================ */}
      <header className="px-6 py-5 border-b border-slate-100 flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-slate-900 text-white grid place-items-center flex-shrink-0">
          <span className="block w-5 h-5">
            <IconShare className="w-full h-full" />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-slate-900 font-semibold text-base leading-tight">
            Tu invitación está lista
          </h3>
          <p className="text-slate-500 text-sm mt-0.5">
            Comparte el enlace con tus invitados.
          </p>
        </div>
      </header>

      <div className="p-6 space-y-6">
        {/* ============================ LINK ============================ */}
        <div>
          <label className="block text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-500 mb-2">
            Enlace público
          </label>
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-2 focus-within:border-slate-900 focus-within:bg-white transition-colors">
            <span className="w-8 h-8 grid place-items-center rounded-md bg-white border border-slate-200 text-slate-400 flex-shrink-0">
              <span className="block w-3.5 h-3.5">
                <IconLink className="w-full h-full" />
              </span>
            </span>
            <input
              readOnly
              value={publicUrl}
              className="flex-1 min-w-0 bg-transparent text-slate-700 text-sm outline-none font-mono truncate"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              onClick={copy}
              aria-label="Copiar enlace"
              className={`flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-lg transition-colors flex-shrink-0 ${
                copied
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-900 text-white hover:bg-slate-800'
              }`}
            >
              <span className="block w-3.5 h-3.5">
                {copied ? <IconCheck className="w-full h-full" /> : <IconCopy className="w-full h-full" />}
              </span>
              <span className="hidden sm:inline">{copied ? 'Copiado' : 'Copiar'}</span>
            </button>
          </div>
        </div>

        {/* ============================ COMPARTIR + ACCIONES ============================ */}
        <div className="grid sm:grid-cols-2 gap-6">
          {/* Redes sociales */}
          <div>
            <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-500 mb-3">
              Compartir en
            </p>
            <div className="flex flex-wrap gap-2">
              {SOCIALS.map(({ key, label, color, Icon, buildUrl }) => (
                <a
                  key={key}
                  href={buildUrl(publicUrl, title)}
                  target="_blank"
                  rel="noreferrer"
                  title={label}
                  aria-label={`Compartir por ${label}`}
                  className="group relative inline-flex items-center justify-center w-10 h-10 rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm transition-all"
                >
                  <span className="block w-4 h-4 transition-transform group-hover:scale-110" style={{ color }}>
                    <Icon className="w-full h-full" />
                  </span>
                  <span className="hidden md:block absolute -top-9 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md text-[10px] font-medium bg-slate-900 text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    {label}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Acciones */}
          <div>
            <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-500 mb-3">
              Acciones
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3.5 py-2.5 rounded-lg transition-colors"
              >
                <span className="block w-3.5 h-3.5">
                  <IconEye className="w-full h-full" />
                </span>
                Ver
              </a>

              <button
                type="button"
                onClick={() => setShowQr((v) => !v)}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2.5 rounded-lg transition-colors border ${
                  showQr
                    ? 'bg-slate-900 border-slate-900 text-white'
                    : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'
                }`}
              >
                <span className="block w-3.5 h-3.5">
                  <IconQr className="w-full h-full" />
                </span>
                {showQr ? 'Ocultar QR' : 'Ver QR'}
              </button>

              {typeof navigator !== 'undefined' && navigator.share && (
                <button
                  type="button"
                  onClick={handleNativeShare}
                  className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3.5 py-2.5 rounded-lg transition-colors"
                >
                  Compartir
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ============================ QR PANEL ============================ */}
        {showQr && (
          <div className="pt-6 border-t border-slate-100">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div
                ref={qrRef}
                className="flex-shrink-0 bg-white p-4 rounded-xl border border-slate-200"
              >
                <QRCodeCanvas
                  value={publicUrl}
                  size={160}
                  level="H"
                  marginSize={0}
                  fgColor="#0f172a"
                  bgColor="#ffffff"
                />
              </div>

              <div className="flex-1 text-center sm:text-left">
                <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-500 mb-1">
                  Código QR
                </p>
                <h4 className="text-slate-900 font-semibold text-base leading-snug mb-2">
                  Tus invitados pueden escanear
                </h4>
                <p className="text-sm text-slate-500 leading-relaxed mb-4">
                  Imprímelo en las tarjetas o compártelo en tus redes. Al escanear, se abre la invitación.
                </p>

                <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                  <button
                    type="button"
                    onClick={downloadQr}
                    className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3.5 py-2.5 rounded-lg transition-colors"
                  >
                    <span className="block w-3.5 h-3.5">
                      <IconDownload className="w-full h-full" />
                    </span>
                    Descargar PNG
                  </button>
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3.5 py-2.5 rounded-lg transition-colors"
                  >
                    Probar enlace
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================ FOOTER ============================ */}
        <p className="text-[11px] text-slate-400 leading-relaxed pt-2 border-t border-slate-100">
          Este enlace es único para tu evento. Cada invitado verá la misma invitación personalizada.
        </p>
      </div>
    </section>
  );
}